import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Pages
import HomePage from './pages/HomePage';
import JobsListPage from './pages/JobsListPage';
import JobDetailPage from './pages/JobDetailPage';
import VolunteerDashboardPage from './pages/VolunteerDashboardPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AdminPage from './pages/AdminPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import CreateJobPage from './pages/CreateJobPage';
import EditJobPage from './pages/EditJobPage';
import JobDetailAdminPage from './pages/JobDetailAdminPage';
import CreateTimeslotPage from './pages/CreateTimeslotPage';
import EditTimeslotPage from './pages/EditTimeslotPage';
import VolunteerDetailAdminPage from './pages/VolunteerDetailAdminPage';
import EditRegistrationPage from './pages/EditRegistrationPage';
import JobManagerPage from './pages/JobManagerPage';
import ProfilePage from './pages/ProfilePage';
import RoleManagementPage from './pages/RoleManagementPage';
import QRCodeManagementPage from './pages/QRCodeManagementPage';
import QRCodeScannerPage from './pages/QRCodeScannerPage';
import NotFoundPage from './pages/NotFoundPage';

// Components
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';

// Context
import { AuthProvider } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#066137', // Forest green color from Strudengauer Messe
      light: '#0b8a4f',
      dark: '#044626',
    },
    secondary: {
      main: '#f8b804', // Gold accent color from website
      light: '#ffd54f',
      dark: '#c68f00',
    },
    background: {
      default: '#f7fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#2d3748',
      secondary: '#4a5568',
    },
  },
  typography: {
    fontFamily: [
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif'
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '0.01em',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0.375rem',
          padding: '0.5rem 1rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#066137', // Primary green
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '0.375rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.375rem',
          },
        },
      },
    },
  },
});

function App() {
  // API interceptors are now set up directly in the api.ts file
  // No need for initialization here anymore

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Header />
            <Box component="main" sx={{ minHeight: 'calc(100vh - 64px)', pt: 2 }}>
              <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/jobs" element={<JobsListPage />} />
                  <Route path="/jobs/:jobId" element={<JobDetailPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                    {/* Volunteer protected routes */}
                  <Route path="/volunteer/dashboard" element={
                    <ProtectedRoute>
                      <VolunteerDashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/volunteer/profile" element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Job Manager protected routes */}
                  <Route path="/job-manager" element={
                    <ProtectedRoute requireJobManager={true}>
                      <JobManagerPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* QR Code Manager protected routes */}
                  <Route path="/qr-codes" element={
                    <ProtectedRoute requireQRCodeManager={true}>
                      <QRCodeManagementPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/qr-scanner" element={
                    <ProtectedRoute requireQRCodeManager={true}>
                      <QRCodeScannerPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin protected routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/jobs/create" element={
                    <ProtectedRoute requireAdmin={true}>
                      <CreateJobPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/jobs/edit/:jobId" element={
                    <ProtectedRoute requireAdmin={true}>
                      <EditJobPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/jobs/:jobId" element={
                    <ProtectedRoute requireAdmin={true}>
                      <JobDetailAdminPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/jobs/:jobId/timeslots/create" element={
                    <ProtectedRoute requireAdmin={true}>
                      <CreateTimeslotPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/timeslots/edit/:timeslotId" element={
                    <ProtectedRoute requireAdmin={true}>
                      <EditTimeslotPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/volunteers/:volunteerId" element={
                    <ProtectedRoute requireAdmin={true}>
                      <VolunteerDetailAdminPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/registrations/edit/:registrationId" element={
                    <ProtectedRoute requireAdmin={true}>
                      <EditRegistrationPage />
                    </ProtectedRoute>                  } />
                  <Route path="/admin/job-manager" element={
                    <ProtectedRoute requireAdmin={true}>
                      <JobManagerPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/roles" element={
                    <ProtectedRoute requireAdmin={true}>
                      <RoleManagementPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/qr-codes" element={
                    <ProtectedRoute requireAdmin={true}>
                      <QRCodeManagementPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/qr-scanner" element={
                    <ProtectedRoute requireAdmin={true}>
                      <QRCodeScannerPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Fallback 404 route */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Box>
            </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
